<template>
  <div class="insure-page">
    <div class="page-top">
      <h2 class="page-title">设置服务流程</h2>
    </div>

    <div class="service-section">
      <div class="service-title">医疗救援</div>
      <div class="service-areas">
        <div
          v-for="item in serviceList"
          :key="item.id"
          class="area-item"
          @click="openEdit(item)"
        >
          <div class="area-placeholder">{{ item.label }}</div>
          <div class="area-edit">编辑</div>
        </div>
      </div>
    </div>

    <el-dialog
      :title="'编辑服务流程' + (currentParentName ? ' - ' + currentParentName : '')"
      :visible.sync="dialogVisible"
      width="680px"
    >
      <div v-loading="editLoading" class="edit-content">
        <div class="edit-areas">
          <div
            v-for="option in childList"
            :key="option.id"
            class="edit-area-item"
            :class="{ selected: selectedIds.indexOf(Number(option.id)) > -1 }"
            @click="toggleChild(option)"
          >
            <img
              :src="getOptionIcon(option)"
              class="edit-area-icon"
              alt=""
              @error="onIconError($event, option)"
            />
            <p class="edit-area-text">{{ option.name }}</p>
          </div>
        </div>
        <p v-if="!editLoading && !childList.length" class="empty-tip">暂无子服务项</p>
      </div>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveEdit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { wToEditService, updateServices } from "@/views/axios/api.js";
import { isBizJson } from "@/util/insureApiCompat.js";

/** 本地服务图标目录（含选中态 _ed） */
var iconCtx = require.context("@/assets/img/images", false, /\.png$/);

/**
 * 按服务中文名映射本地图标文件名（不含扩展名）
 * 覆盖医疗转运/送返及常见子服务
 */
var NAME_ICON_MAP = {
  紧急救援: "jjjy",
  紧急就医: "jjjy",
  医疗咨询: "ylzx",
  药品配送: "ypds",
  住院安排: "apzy",
  住院信息计划: "zyxxjh",
  手术安排: "sjyldj",
  康复指导: "bsxt",
  紧急医疗转运: "jjylzy",
  医疗转运: "jjylzy",
  医疗送返: "ylsf",
  遗体送返: "ytsf",
  骨灰送返: "ytsf",
  遗体火化: "ythh",
  子女送返: "znsf",
  亲属探望: "qstw",
  亲友探望: "qytw",
  制定交通转运方案: "zdjtzyfa",
  人员代办: "rydb",
  费用代办: "fydb",
  住院费用代办: "zyfydb",
  法律援助: "flyz",
  安保服务: "abfw",
  星使服务: "xsfw",
  医疗监控: "yljk",
  医疗计划: "yljh",
  住院全程监控: "zyqcjk",
};

/**
 * 读取本地 png 图标
 * @param {string} fileName 文件名，如 jjylzy.png
 * @returns {string|null}
 */
function loadLocalIcon(fileName) {
  var key = "./" + fileName;
  if (iconCtx.keys().indexOf(key) > -1) return iconCtx(key);
  return null;
}

/**
 * 解析本地图标（选中用 _ed）
 * @param {string} baseName 不含扩展名，可带 _ed
 * @param {boolean} selected 是否选中
 * @returns {string}
 */
function resolveLocalByBase(baseName, selected) {
  var base = String(baseName || "").replace(/_ed$/i, "");
  if (!base) return loadLocalIcon("moren.png");
  var preferred = selected ? base + "_ed.png" : base + ".png";
  var fallback = base + ".png";
  return loadLocalIcon(preferred) || loadLocalIcon(fallback) || loadLocalIcon("moren.png");
}

/**
 * 设置服务流程（根节点固定 1/100/200/300/400/500）
 */
export default {
  name: "InsureServiceSetting",
  data() {
    return {
      token:
        this.$route.params.token ||
        JSON.parse(window.localStorage.getItem("data") || "{}").data,
      editLoading: false,
      saving: false,
      dialogVisible: false,
      currentParentId: "",
      currentParentName: "",
      childList: [],
      selectedIds: [],
      /** 现网固定根节点 */
      serviceList: [
        { id: 1, label: "门急诊就医" },
        { id: 100, label: "住院安排" },
        { id: 200, label: "医疗送返/转运" },
        { id: 300, label: "其他医疗救援" },
        { id: 400, label: "其他服务" },
        { id: 500, label: "其他服务" },
      ],
    };
  },
  methods: {
    /**
     * 打开编辑弹窗
     * @param {Object} item 根服务项
     */
    openEdit(item) {
      this.currentParentId = item.id;
      this.currentParentName = item.label;
      this.dialogVisible = true;
      this.editLoading = true;
      this.childList = [];
      this.selectedIds = [];
      wToEditService({ token: this.token, parentId: item.id })
        .then((res) => {
          if (!isBizJson(res.data) || Number(res.data.code) !== 0) return;
          this.currentParentName = (res.data.obj && res.data.obj.name) || item.label;
          this.childList = res.data.obj2 || [];
          this.selectedIds = this.childList
            .filter(function (c) {
              return Number(c.status) === 1;
            })
            .map(function (c) {
              return Number(c.id);
            });
        })
        .finally(() => {
          this.editLoading = false;
        });
    },

    /**
     * 从远程 icon URL 提取本地文件名（不含扩展名）
     * @param {string} iconUrl
     * @returns {string}
     */
    extractIconBase(iconUrl) {
      if (!iconUrl) return "";
      var match = String(iconUrl).match(/([^/?#]+)\.(png|jpe?g|gif|webp|svg)(?:$|[?#])/i);
      return match ? match[1].replace(/_ed$/i, "") : "";
    },

    /**
     * 按服务名模糊匹配本地图标
     * @param {string} name
     * @returns {string}
     */
    matchIconByName(name) {
      if (!name) return "";
      if (NAME_ICON_MAP[name]) return NAME_ICON_MAP[name];
      var keys = Object.keys(NAME_ICON_MAP);
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        if (name.indexOf(key) > -1 || key.indexOf(name) > -1) {
          return NAME_ICON_MAP[key];
        }
      }
      return "";
    },

    /**
     * 子项图标：优先本地资源，避免远程破损图
     * @param {Object} option 子服务项
     * @returns {string}
     */
    getOptionIcon(option) {
      var selected = this.selectedIds.indexOf(Number(option.id)) > -1;
      if (option._localIconBroken) {
        return resolveLocalByBase("moren", selected);
      }
      var fromUrl = this.extractIconBase(option.icon);
      if (fromUrl && loadLocalIcon(fromUrl + ".png")) {
        return resolveLocalByBase(fromUrl, selected);
      }
      var fromName = this.matchIconByName(option.name);
      if (fromName) {
        return resolveLocalByBase(fromName, selected);
      }
      return resolveLocalByBase("moren", selected);
    },

    /**
     * 图标加载失败时回退默认图
     * @param {Event} e
     * @param {Object} option
     */
    onIconError(e, option) {
      option._localIconBroken = true;
      var selected = this.selectedIds.indexOf(Number(option.id)) > -1;
      e.target.src = resolveLocalByBase("moren", selected);
    },

    /**
     * 切换子服务选中
     * @param {Object} option 子服务项
     */
    toggleChild(option) {
      var id = Number(option.id);
      var idx = this.selectedIds.indexOf(id);
      if (idx > -1) this.selectedIds.splice(idx, 1);
      else this.selectedIds.push(id);
    },

    /**
     * 保存服务流程勾选
     */
    saveEdit() {
      this.saving = true;
      updateServices({
        token: this.token,
        parentId: this.currentParentId,
        ids: this.selectedIds.join(","),
      })
        .then((res) => {
          if (!isBizJson(res.data) || Number(res.data.code) !== 0) return;
          this.$message.success("保存成功");
          this.dialogVisible = false;
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.insure-page {
  text-align: left;
  padding: 8px 8px 24px;
}
.page-top {
  margin-bottom: 16px;
}
.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
.service-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
}
.service-areas {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.area-item {
  width: 160px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
}
.area-item:hover {
  box-shadow: 0 4px 16px rgba(0, 171, 250, 0.16);
}
.area-placeholder {
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8f7ff, #f5f7fb);
  color: #00abfa;
  font-weight: 600;
  padding: 8px;
  text-align: center;
  font-size: 14px;
}
.area-name,
.area-edit {
  text-align: center;
  padding: 8px;
  font-size: 13px;
}
.area-edit {
  color: #00abfa;
  /* padding-top: 0; */
}
.edit-areas {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.edit-area-item {
  width: 110px;
  padding: 12px 8px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  text-align: center;
}
.edit-area-item.selected {
  border-color: #00abfa;
  background: #e8f7ff;
}
.edit-area-icon {
  width: 36px;
  height: 36px;
  margin: 0 auto 8px;
  display: block;
  object-fit: contain;
}
.edit-area-text {
  margin: 0;
  font-size: 12px;
}
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 24px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/cmd_center/serve.vue